import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Input, Modal, Table, Typography } from '@duxton-web/ui';
import type { TableColumnsType } from '@duxton-web/ui';

interface ServiceRow {
  key: string;
}

const SERVICE_TABLE_COLUMNS: TableColumnsType<ServiceRow> = [
  {
    title: 'Service',
    dataIndex: 'key',
    key: 'key',
  },
];

export interface ServiceSelectionModalProps {
  open: boolean;
  title?: string;
  services: string[];
  confirmLoading?: boolean;
  onCancel: () => void;
  onOk: (skipServices: string[]) => void;
}

const ServiceSelectionModal: React.FC<ServiceSelectionModalProps> = ({
  open,
  title = 'Select Services',
  services,
  confirmLoading,
  onCancel,
  onOk,
}) => {
  const [deselectedServices, setDeselectedServices] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) {
      setDeselectedServices(new Set());
      setSearchTerm('');
    }
  }, [open]);

  const filteredServices = useMemo(
    () =>
      services.filter(
        (s) =>
          !searchTerm || s.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [services, searchTerm],
  );

  const serviceRows = useMemo<ServiceRow[]>(
    () => filteredServices.map((s) => ({ key: s })),
    [filteredServices],
  );

  const selectedRowKeys = useMemo(
    () => filteredServices.filter((s) => !deselectedServices.has(s)),
    [filteredServices, deselectedServices],
  );

  const handleSelectionChange = (newSelectedKeys: React.Key[]) => {
    const newSelectedSet = new Set(newSelectedKeys.map(String));
    setDeselectedServices((prev) => {
      const next = new Set(prev);
      filteredServices.forEach((s) => {
        if (newSelectedSet.has(s)) {
          next.delete(s);
        } else {
          next.add(s);
        }
      });
      return next;
    });
  };

  const handleOk = () => {
    const skipServices = services.filter((s) => deselectedServices.has(s));
    onOk(skipServices);
  };

  return (
    <Modal
      centered
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      cancelText="Cancel"
      okText="Ok"
      cancelButtonProps={{ size: 'small' }}
      okButtonProps={{ size: 'small', loading: confirmLoading }}
      width={700}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      destroyOnClose
    >
      <Flex vertical gap={12}>
        <Input.Search
          placeholder="Search services..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {selectedRowKeys.length} of {services.length} services selected.
          Unselected services will be skipped.
        </Typography.Text>
        <Table<ServiceRow>
          virtual
          columns={SERVICE_TABLE_COLUMNS}
          dataSource={serviceRows}
          rowSelection={{
            type: 'checkbox',
            columnWidth: 32,
            selectedRowKeys,
            onChange: handleSelectionChange,
          }}
          pagination={false}
          scroll={{ y: 'calc(70vh - 160px)' }}
          size="small"
        />
      </Flex>
    </Modal>
  );
};

export default ServiceSelectionModal;
